// frontend/src/components/staff/RecipientPickerDrawer.js
//
// MAILCHIMP-COMMS-001-B — Recipient picker (slide-in drawer, right side).
// Spec: docs/design/MAILCHIMP-COMMS-001-A-design.md §4.
//
// Behavior:
//   - Search input filters customers via GET /staff/customers/search
//     (existing staffAPI.searchCustomers helper).
//   - Multi-select via checkbox rows (entire row is the touch target).
//   - Selections accumulate locally; on Add commit, full set is pushed
//     to the Compose page (replacing the existing chip group). On Cancel
//     commit, no change.
//   - At staff role cap (50): rows beyond cap render disabled at 50%
//     opacity with a tooltip explaining the limit.
//   - Empty state and "no search results" state per spec §4.5.

import React from "react";
import { FaSearch, FaTimes, FaUserSlash } from "react-icons/fa";
import { staffAPI } from "../../services/api";
import "./RecipientPickerDrawer.css";

const SEARCH_DEBOUNCE_MS = 200;
const MIN_QUERY_LENGTH = 2;

const RecipientPickerDrawer = ({
  initialSelection = [],
  recipientCap,
  role,
  onCancel,
  onCommit,
}) => {
  const [query, setQuery] = React.useState("");
  const [results, setResults] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  // Local staging — independent from the parent until Commit. Keyed by
  // userId or email for fast toggle lookup.
  const [staging, setStaging] = React.useState(() => {
    const map = new Map();
    initialSelection.forEach((r) => {
      const key = r.userId || r.email?.toLowerCase();
      if (key) map.set(key, r);
    });
    return map;
  });

  const stagingArray = React.useMemo(() => Array.from(staging.values()), [
    staging,
  ]);
  const atCap = stagingArray.length >= recipientCap;

  // Search effect — debounced. Min 2 chars. Empty query clears results.
  React.useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setError("");
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    const handle = setTimeout(() => {
      staffAPI
        .searchCustomers({ search: trimmed, limit: 50 })
        .then((response) => {
          if (cancelled) return;
          // The /staff/customers/search endpoint returns a paginated or
          // flat shape depending on backend version. Defensive extract.
          const raw = response?.data;
          let list = [];
          if (Array.isArray(raw)) list = raw;
          else if (Array.isArray(raw?.customers)) list = raw.customers;
          else if (Array.isArray(raw?.results)) list = raw.results;
          else if (Array.isArray(raw?.data)) list = raw.data;
          setResults(list);
          setLoading(false);
        })
        .catch((err) => {
          if (cancelled) return;
          setError(
            err?.userMessage ||
              "Couldn't search customers. Try again in a moment."
          );
          setResults([]);
          setLoading(false);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query]);

  // Escape closes the drawer (with confirm if any selections pending vs
  // initial). For simplicity in v1, Esc just dismisses — Add commits.
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCancel?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const toggleCustomer = (customer) => {
    const key = customer._id || customer.id || customer.email?.toLowerCase();
    if (!key) return;
    setStaging((prev) => {
      const next = new Map(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        if (next.size >= recipientCap) {
          // At cap — don't add. The row's disabled state already prevents
          // the click visually, but defense-in-depth here matters because
          // a fast keyboard user could race the disabled-attr.
          return prev;
        }
        next.set(key, {
          userId: customer._id || customer.id || null,
          email: customer.email,
          displayName:
            customer.displayName ||
            customer.fullName ||
            joinName(customer.firstName, customer.lastName) ||
            customer.email,
          isMember: Boolean(customer.isMember || customer.membershipActive),
        });
      }
      return next;
    });
  };

  const handleCommit = () => {
    onCommit(stagingArray);
  };

  return (
    <div
      className="broadcast-drawer-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Add recipients"
      onClick={(e) => {
        // Backdrop click — close. (No confirm in v1 — staging is preserved
        // ephemerally only; the Compose page chip group is unchanged on
        // backdrop dismiss, same as explicit Cancel.)
        if (e.target === e.currentTarget) {
          onCancel?.();
        }
      }}
    >
      <aside className="broadcast-drawer" aria-label="Add recipients">
        <header className="broadcast-drawer-header">
          <h2>Add recipients</h2>
          <button
            type="button"
            className="broadcast-drawer-close"
            onClick={onCancel}
            aria-label="Close recipient picker"
          >
            <FaTimes aria-hidden="true" />
          </button>
        </header>

        <div className="broadcast-drawer-search">
          <FaSearch className="broadcast-drawer-search-icon" aria-hidden="true" />
          <input
            type="search"
            className="broadcast-drawer-search-input"
            placeholder="Name or email"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
            aria-label="Search customers by name or email"
          />
        </div>

        <div className="broadcast-drawer-cap-row" aria-live="polite">
          {stagingArray.length} of {recipientCap} selected
          {role === "staff" && " (staff limit)"}
        </div>

        <div className="broadcast-drawer-results" role="listbox" aria-multiselectable="true">
          {query.trim().length < MIN_QUERY_LENGTH && (
            <div className="broadcast-drawer-empty">
              Type at least 2 characters to search customers.
            </div>
          )}

          {query.trim().length >= MIN_QUERY_LENGTH && loading && (
            <div className="broadcast-drawer-empty">Searching...</div>
          )}

          {query.trim().length >= MIN_QUERY_LENGTH && error && !loading && (
            <div className="broadcast-drawer-error" role="alert">
              {error}
            </div>
          )}

          {query.trim().length >= MIN_QUERY_LENGTH &&
            !loading &&
            !error &&
            results.length === 0 && (
              <div className="broadcast-drawer-empty">
                <FaUserSlash aria-hidden="true" />
                <span>No customers found for "{query}"</span>
              </div>
            )}

          {!loading &&
            !error &&
            results.map((customer) => {
              const key =
                customer._id || customer.id || customer.email?.toLowerCase();
              const isSelected = staging.has(key);
              const disabledByCapacity = !isSelected && atCap;
              const displayName =
                customer.displayName ||
                customer.fullName ||
                joinName(customer.firstName, customer.lastName) ||
                customer.email;
              return (
                <div
                  key={key}
                  role="checkbox"
                  aria-checked={isSelected}
                  aria-disabled={disabledByCapacity}
                  tabIndex={disabledByCapacity ? -1 : 0}
                  className={`broadcast-drawer-row ${
                    isSelected ? "selected" : ""
                  } ${disabledByCapacity ? "disabled" : ""}`}
                  onClick={() => {
                    if (disabledByCapacity) return;
                    toggleCustomer(customer);
                  }}
                  onKeyDown={(e) => {
                    if (disabledByCapacity) return;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleCustomer(customer);
                    }
                  }}
                  title={
                    disabledByCapacity
                      ? `Send limit reached: ${role} can send to up to ${recipientCap} customers per broadcast`
                      : undefined
                  }
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    readOnly
                    tabIndex={-1}
                    aria-hidden="true"
                  />
                  <div className="broadcast-drawer-row-meta">
                    <div className="broadcast-drawer-row-name">
                      {displayName}
                    </div>
                    <div className="broadcast-drawer-row-sub">
                      {customer.email}
                      {customer.isMember || customer.membershipActive
                        ? " . Member"
                        : " . Non-member"}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>

        <footer className="broadcast-drawer-footer">
          <button
            type="button"
            className="broadcast-btn-outline"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="broadcast-btn-primary"
            onClick={handleCommit}
            disabled={stagingArray.length === 0}
            aria-disabled={stagingArray.length === 0}
          >
            Add {stagingArray.length} to list
          </button>
        </footer>
      </aside>
    </div>
  );
};

function joinName(first, last) {
  const f = (first || "").trim();
  const l = (last || "").trim();
  if (!f && !l) return "";
  if (!l) return f;
  if (!f) return l;
  return `${f} ${l}`;
}

export default RecipientPickerDrawer;
